import { IEventAggregationService } from '@studyportals/event-aggregation-service-interface';
import { IAppRegistration, IRouting, IsAppRegistration, SearchType } from '@studyportals/search-page-interface';
import { Ref, App } from 'vue';
import {
	createRouter,
	createWebHistory,
	Router,
	LocationQueryRaw,
	RouteLocationNormalized,
	NavigationGuardNext,
	RouteLocationNormalizedLoaded,
	RouteRecordRaw,
	RouteLocationRaw,
	RouteLocation,
	RouterOptions,
	LocationQuery,
	RouteParamsRawGeneric,
	RouteLocationNormalizedLoadedGeneric,
} from 'vue-router';
import { EventAggregationService } from '@/search-page/platform/EventAggregationService';
import { SearchPageNavigationPerformedEvent } from './events/SearchPageNavigationPerformedEvent';
import { match, MatchResult } from 'path-to-regexp';

const MAP_INDEX_TO_STRING_APPLICATION_TYPE: Record<string, string> = {
	[SearchType.ORGANISATION]: 'universities',
	[SearchType.SCHOLARSHIP]: 'scholarships',
};

export class Routing implements IRouting {
	private static MappedRoutes: Record<string, string> = {};
	private readonly eventAggregationService: IEventAggregationService;
	private readonly applications: IAppRegistration[] = [];
	private initialNavigationPerformed = false;
	public readonly router: Router;

	public constructor(eventAggregationService?: IEventAggregationService) {
		this.eventAggregationService = eventAggregationService ?? EventAggregationService.instance;

		const options: RouterOptions = {
			history: createWebHistory('/search'),
			routes: [],
			stringifyQuery: Routing.stringifyQuery.bind(undefined),
			parseQuery: Routing.parseQuery.bind(undefined),
		};

		this.router = createRouter(options);
		this.router.beforeEach(this.beforeEach.bind(this));
		this.router.afterEach(this.afterEach.bind(this));
	}

	public get currentRoute(): Ref<RouteLocationNormalizedLoaded> {
		return this.router.currentRoute;
	}

	public get currentApplication(): IAppRegistration | null {
		return this.findApplicationFor(this.currentRoute.value);
	}

	public async navigateTo(location: RouteLocationRaw): Promise<void> {
		await this.router.push(location);
	}

	public resolve(location: RouteLocationRaw): Promise<RouteLocation & { href: string; }> {
		return Promise.resolve(this.router.resolve(location));
	}

	public registerApplication(application: IAppRegistration): void {
		const isNotAppRegistration = !IsAppRegistration(application);
		if (isNotAppRegistration) {
			return;
		}

		this.applications.push(application);

		for (const route of application.routing.routes) {
			this.router.addRoute(route);
		}

		this.populateRoutesMapping(this.router.getRoutes());

		const currentRouteIsNotRoot = this.router.currentRoute.value.path !== '/';
		const currentRouteIsNotMatched = this.router.currentRoute.value.matched.length === 0;
		const applicationForRouteAlreadyRegistered = this.findApplicationFor(this.router.currentRoute.value) !== null;

		const shouldPerformInitialNavigation = !this.initialNavigationPerformed && currentRouteIsNotRoot
			&& currentRouteIsNotMatched && applicationForRouteAlreadyRegistered;

		if (shouldPerformInitialNavigation) {
			this.initialNavigationPerformed = true;
			const route = this.getRouteToNavigateTo(this.router.currentRoute.value, application.type);

			void this.navigateTo(route);
		}
	}

	private populateRoutesMapping(routes: RouteRecordRaw[]): void {
		const allRoutes = routes.flatMap((r) => this.collectRoutesFrom(r));

		for (const route of allRoutes) {
			if (Routing.MappedRoutes[route.path]) continue;

			const routeKey = route.path.replace(/\?/g, '');
			Routing.MappedRoutes[routeKey] = route.name as string;
		}
	}

	private getRouteToNavigateTo(route: RouteLocationNormalizedLoadedGeneric, applicationType: SearchType): RouteLocationRaw {
		const path = route.path;

		for (const [routePath, name] of Object.entries(Routing.MappedRoutes)) {
			if (applicationType !== SearchType.STUDY &&
				!routePath.includes(MAP_INDEX_TO_STRING_APPLICATION_TYPE[applicationType])) {
				continue;
			}

			const matcher = match(routePath, { decode: decodeURIComponent }) as (path: string) => MatchResult<object>;
			const matchResults = matcher(path);

			if (matchResults) {
				return {
					...route,
					name,
					params: matchResults.params as RouteParamsRawGeneric,
				} as RouteLocationRaw;
			}
		}

		return route;
	}

	private async beforeEach(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> {
		const application = this.findApplicationFor(to);
		if (application) {
			await application.routing.beforeEach(to, from, next);
		}
		else {
			next();
		}
	}

	private async afterEach(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<void> {
		const application = this.findApplicationFor(to);

		this.eventAggregationService.publishTo(
			SearchPageNavigationPerformedEvent.EventType,
			new SearchPageNavigationPerformedEvent(to, application),
		);

		if (application) {
			await application.routing.afterEach(to, from);
		}
	}

	private findApplicationFor(location: RouteLocationNormalized): IAppRegistration | null {
		const matchedRoutePaths = new Set(location.matched.map((r) => r.path));

		if (matchedRoutePaths.size === 0) {
			const matchedApplication = this.applications.find(
				(a) => a.isPathForThisApp?.(location.path)
			);

			return matchedApplication ?? null;
		}

		for (const application of this.applications) {

			const matchedRoute = application.routing.routes
				.flatMap((r) => this.collectRoutesFrom(r))
				.find((route) => matchedRoutePaths.has(route.path));

			if (matchedRoute) return application;
		}
		return null;
	}

	private collectRoutesFrom(route: RouteRecordRaw): RouteRecordRaw[] {
		const result: RouteRecordRaw[] = [];

		const stack: RouteRecordRaw[] = [route];
		while (stack.length > 0) {
			const current = stack.pop()!;
			result.push(current);

			if (current.children) {
				stack.push(...current.children);
			}
		}

		return result;
	}

	public install(app: App): void {
		app.use(this.router);
	}

	private static stringifyQuery(query: LocationQueryRaw): string {
		const filterKeys = Object.keys(query);
		if (filterKeys.length > 0) {
			const parameters: string[] = [];

			for (const filterKey of filterKeys) {
				const valueRaw = query[filterKey];
				if (valueRaw === undefined || valueRaw === null) {
					continue;
				}

				const value = Array.isArray(valueRaw) ? valueRaw.join(',') : valueRaw;
				parameters.push(`${filterKey}=${value}`);
			}

			return parameters.join('&');
		}

		return '';
	}

	private static parseQuery(search: string): LocationQuery {
		const searchNormalized = decodeURIComponent(search.trim());
		if (searchNormalized.length === 0) {
			return {};
		}

		const filters = searchNormalized.split('&');
		const result: LocationQuery = {};

		for (const filter of filters) {
			const [key, valueEncoded] = filter.split('=');

			let values: string[] = [];
			if (valueEncoded) {
				const intervals = valueEncoded.match(/\[.*?\]/g);
				values = intervals !== null ? intervals : valueEncoded.split(',');
			}

			if (values.length > 1) {
				result[key] = values;
			} else {
				result[key] = values[0];
			}
		}

		return result;
	}
}
